import {
  IconButton,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import SvgIcon from "../../components/Images/SvgIcon";
import moment from "moment";
import {
  startLoader,
  stopLoader,
  showToast,
  open_dialog,
} from "../../lib/global";
import { DELETE_PAGE_MODEL } from "../../lib/constant";
import { breadcrumbAction } from "../../redux/actions/config.action";
import { getAllPages, updatePageById } from "../../redux/actions/pages.action";
import ablity from "../../helpers/abilityGuard";
import { IOSSwitch } from "../../components/switch/IOSSwitch";

function PagesList() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { type } = useParams(); // 'section_promotion', 'deal', or 'page'
  const [pagesData, setPagesData] = useState([]);
  const [offset, setOffset] = useState(0);
  const [limit] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const getPageTitle = () => {
    if (type === "section_promotion") return "SECTION PROMOTIONS";
    if (type === "deal") return "DEALS PROMOTIONS";
    return "PAGES";
  };

  const getPageDescription = () => {
    if (type === "section_promotion") return "Manage section promotion pages with collections and custom checkout promotions.";
    if (type === "deal") return "Manage special deals, campaign offerings, and promotional pages.";
    return "Manage custom static content pages.";
  };

  const fetchPages = () => {
    startLoader();
    dispatch(getAllPages({ limit, offset, type }))
      .then((res) => {
        if (res && res.data && res.data.status === 200) {
          setPagesData(res.data.data || []);
          setTotalCount(res.data.count || 0);
        } else {
          setPagesData([]);
          setTotalCount(0);
        }
        setTimeout(() => stopLoader(), 500);
      })
      .catch((err) => {
        console.error("Error fetching pages:", err);
        setPagesData([]);
        setTotalCount(0);
        stopLoader();
      });
  };

  useEffect(() => {
    const pageTitle = getPageTitle();
    const breadcrumb = [{ label: pageTitle, url: null }];
    dispatch(breadcrumbAction(breadcrumb));
  }, [dispatch, type]);

  useEffect(() => {
    fetchPages();
  }, [offset, type]);

  const handlePageChange = (event, value) => {
    setOffset(value - 1);
  };

  const handleStatusToggle = (id, currentStatus) => {
    startLoader();
    const nextStatus = currentStatus === "publish" ? "draft" : "publish";
    dispatch(updatePageById(id, { status: nextStatus }))
      .then((res) => {
        stopLoader();
        showToast("Status updated successfully", "success");
        fetchPages();
      })
      .catch((err) => {
        stopLoader();
        console.error("Error updating status:", err);
        showToast("Failed to update status", "error");
      });
  };

  const handleDeleteClick = (id) => {
    open_dialog(DELETE_PAGE_MODEL, {
      onSubmit: fetchPages,
      id,
    });
  };

  return (
    <React.Fragment>
      <div className="card mx-3 p-3 shadow-sm border-0">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h5 className="font-weight-bold mb-1 text-uppercase">{getPageTitle()}</h5>
            <p className="text-muted mb-0 small">
              {getPageDescription()}
            </p>
          </div>
          <div>
            {ablity({ model: "Pages", action: "create" }) === 1 && (
              <button
                type="button"
                onClick={() => history.push(`/pages/${type}/add`)}
                className="btn btn-dark px-4 py-2 text-nowrap rounded-lg shadow-sm"
              >
                + Add {type === "section_promotion" ? "Section Promotion" : type === "deal" ? "Deals Promotion" : "Page"}
              </button>
            )}
          </div>
        </div>

        {/* Info Box */}
        <div className="alert alert-light border d-flex justify-content-between align-items-center mb-4 p-3 rounded-lg">
          <div>
            <span className="badge text-uppercase mb-1 px-2 py-1" style={{ backgroundColor: "#495057", color: "#ffffff" }}>
              PAGE SETTINGS
            </span>
            <div className="font-weight-bold text-dark mt-1">
              Promotional pages with customizable rich text content and SEO configurations.
            </div>
          </div>
          <div className="text-right">
            <div className="small text-muted font-weight-bold">Expected Banner Size</div>
            <div className="text-primary font-weight-bold h6 mb-0">
              Desktop: 1200 x 400 px
            </div>
          </div>
        </div>

        <TableContainer className="border rounded-lg">
          <Table sx={{ minWidth: 650 }} stickyHeader aria-label="sticky table">
            <TableHead className="bg-light">
              <TableRow>
                <TableCell className="th_cell font-weight-bold" align="left" style={{ width: "60px" }}>
                  No.
                </TableCell>
                <TableCell className="th_cell font-weight-bold" align="left">
                  Banner Desktop
                </TableCell>
                <TableCell className="th_cell font-weight-bold" align="left">
                  Title
                </TableCell>
                <TableCell className="th_cell font-weight-bold" align="left">
                  Slug / Link
                </TableCell>
                <TableCell className="th_cell font-weight-bold" align="center" style={{ width: "110px" }}>
                  Order
                </TableCell>
                <TableCell className="th_cell font-weight-bold" align="center">
                  Publish Date
                </TableCell>
                <TableCell className="th_cell font-weight-bold" align="center" style={{ width: "100px" }}>
                  Published
                </TableCell>
                <TableCell className="th_cell font-weight-bold text-center" align="center" style={{ width: "120px" }}>
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagesData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" className="py-5 text-muted">
                    <h5>No pages found</h5>
                    <p className="mb-0 text-secondary">Click the "+ Add Page" button to create one.</p>
                  </TableCell>
                </TableRow>
              ) : (
                pagesData.map((row, i) => (
                  <TableRow key={row.id || i} hover>
                    <TableCell align="left" className="td_cell align-middle">
                      {offset * limit + (i + 1)}
                    </TableCell>
                    <TableCell align="left" className="td_cell align-middle">
                      <img
                        src={row.banner_desktop || "/icons/upload_icon.svg"}
                        alt="banner preview"
                        className="rounded border py-1"
                        style={{ maxWidth: "160px", maxHeight: "80px", objectFit: "contain" }}
                      />
                    </TableCell>
                    <TableCell align="left" className="td_cell align-middle font-weight-bold">
                      {row.title || "-"}
                    </TableCell>
                    <TableCell align="left" className="td_cell align-middle text-primary">
                      <code className="text-break">{row.slug || "-"}</code>
                    </TableCell>
                    <TableCell align="center" className="td_cell align-middle font-weight-bold">
                      {row.order_column !== undefined ? row.order_column : "-"}
                    </TableCell>
                    <TableCell align="center" className="td_cell align-middle">
                      {row.publish_date ? moment(row.publish_date).format("DD MMM YYYY, HH:mm") : "-"}
                    </TableCell>
                    <TableCell align="center" className="td_cell align-middle">
                      {ablity({ model: "Pages", action: "update" }) === 1 ? (
                        <IOSSwitch
                          checked={row.status === "publish"}
                          onChange={() => handleStatusToggle(row.id, row.status)}
                        />
                      ) : (
                        <span className={`badge ${row.status === "publish" ? "bg-success" : "bg-danger"} text-white`}>
                          {row.status === "publish" ? "Published" : "Draft"}
                        </span>
                      )}
                    </TableCell>
                    <TableCell align="center" className="td_cell align-middle">
                      <div className="d-flex justify-content-center align-items-center">
                        {ablity({ model: "Pages", action: "update" }) === 1 && (
                          <IconButton
                            className="pointer px-2"
                            onClick={() => history.push(`/pages/${type}/edit/${row.id}`)}
                          >
                            <SvgIcon src="/icons/edit_icon.svg" alt="edit icon" />
                          </IconButton>
                        )}
                        {ablity({ model: "Pages", action: "delete" }) === 1 && (
                          <IconButton
                            className="pointer px-2"
                            onClick={() => handleDeleteClick(row.id)}
                          >
                            <SvgIcon src="/icons/delete_icon.svg" alt="delete icon" />
                          </IconButton>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {pagesData.length > 0 && (
          <div className="d-flex justify-content-end align-items-center mt-3 pr-2">
            <Stack spacing={2}>
              <Pagination
                count={Math.ceil(totalCount / limit)}
                shape="rounded"
                size="small"
                onChange={handlePageChange}
              />
            </Stack>
          </div>
        )}
      </div>
    </React.Fragment>
  );
}

export default PagesList;
